import { USERS_API_URL } from 'constants/ApiConstants';
import { STATUSES } from 'constants/NotificationsConstants';

import * as types from './actionTypes';

/**
 * @param {string} userId The userId to subscribe to notifications
 * @param {Object} [meta]
 * @returns {import('./types').SubscribeAction} action
 */
export const subscribe = (userId, meta) => ({
  type: types.SUBSCRIBE_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${USERS_API_URL}/${userId}/notifications/subscribe`,
    },
    userId,
  },
  meta,
});
/**
 * @param {import('./types').SubscribePayload} payload
 * @param {Object} [meta]
 * @returns {import('./types').SubscribeActionSuccess}
 */
export const subscribeSuccess = ({ userId, token }, meta) => ({
  type: types.SUBSCRIBE_SUCCESS,
  payload: {
    userId,
    token,
  },
  meta,
});

/**
 * @param {{ error: string, userId: string }} message
 * @param {Object} [meta]
 * @returns {{ error: true } & import('modules/global/generic.types').Action<{ error: string, userId: string }>} action
 */
export const subscribeFailure = ({ error, userId }, meta) => ({
  type: types.SUBSCRIBE_FAILURE,
  payload: { error, userId },
  error: true,
  meta,
});

/**
 * @param {import('./types').SubscribePayload} payload
 * @param {Object} [meta]
 * @returns {import('./types').UnsubscribeAction} action
 */
export const unsubscribe = ({ userId, token }, meta) => ({
  type: types.UNSUBSCRIBE_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${USERS_API_URL}/${userId}/notifications/unsubscribe`,
      data: { token },
    },
  },
  meta,
});
/**
 * @param {null} _ unused at the moment
 * @param {Object} [meta]
 * @returns {import('./types').UnsubscribeActionSuccess} action
 */
export const unsubscribeSuccess = (_, meta) => ({
  type: types.UNSUBSCRIBE_SUCCESS,
  payload: _,
  meta,
});
/**
 * @param {string} message The error message
 * @param {Object} [meta]
 * @returns {import('modules/global/generic.types').ErrorAction} action
 */
export const unsubscribeFailure = (message, meta) => ({
  type: types.UNSUBSCRIBE_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {Array<import('./types').Notification>} notifications The notifications to prepend
 * @param {Object} [meta]
 * @returns {import('./types').AddNotificationAction} action
 */
export const add = (notifications, meta) => ({
  type: types.ADD,
  payload: { notifications },
  meta,
});

/**
 * @param {import('./types').DeleteNotificationPayload} payload
 * @param {Object} [meta]
 * @returns {import('./types').DeleteNotificationAction} action
 */
export const deleteNotification = ({ userId, notificationId }, meta) => ({
  type: types.DELETE_NOTIFICATION,
  payload: {
    request: {
      method: 'DELETE',
      url: `${USERS_API_URL}/${userId}/notifications/${notificationId}`,
    },
    userId,
    notificationId,
  },
  meta,
});
/**
 * @param {string} id The notification id.
 * @param {Object} [meta]
 * @returns {import('./types').DeleteNotificationActionSuccess} action
 */
export const deleteNotificationSuccess = (id, meta) => ({
  type: types.DELETE_NOTIFICATION_SUCCESS,
  payload: id,
  meta,
});
/**
 * @param {string} message The error message
 * @param {string} notificationId The notification id
 * @param {Object} [meta]
 * @returns {import('./types').NotificationErrorAction<Object>} action
 */
export const deleteNotificationFailure = (message, notificationId, meta) => ({
  type: types.DELETE_NOTIFICATION_FAILURE,
  payload: { message, notificationId },
  error: true,
  meta,
});

/**
 * @param {import('./types').FetchNotificationsPayload} page The pagination parameters
 * @param {Object} [meta]
 * @returns {import('./types').FetchNotificationsAction} action
 */
const fetchNotifications = ({ userId, page }, meta) => ({
  type: types.FETCH_NOTIFICATIONS,
  payload: {
    request: {
      method: 'GET',
      url: `${USERS_API_URL}/${userId}/notifications`,
      params: {
        ...page, // To allow search filters parameters
      },
    },
  },
  meta,
});
/**
 * @param {import('./types').FetchNotificationsSuccessParams} payload
 * @param {Object} [meta]
 * @returns {import('./types').FetchNotificationsActionSuccess} action
 */
export const fetchNotificationsSuccess = ({ content: notifications, page }, meta) => ({
  type: types.FETCH_NOTIFICATIONS_SUCCESS,
  payload: {
    notifications,
    page,
  },
  meta,
});
/**
 * @param {string} message The error message
 * @param {Object} [meta]
 * @returns {import('modules/global/generic.types').ErrorAction} action
 */
const fetchNotificationsFailure = (message, meta) => ({
  type: types.FETCH_NOTIFICATIONS_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {import('./types').FetchNotificationsPayload} params The pagination parameters
 * @param {Object} [meta]
 * @returns {import('./types').FetchNotificationsAction} action
 */
export const fetchNotificationsDashboard = (params, meta) => ({
  ...fetchNotifications(params, meta),
  type: types.FETCH_NOTIFICATIONS_DASHBOARD,
});

/**
 * @param {import('./types').FetchNotificationsSuccessParams} response
 * @param {Object} [meta]
 * @returns {import('./types').FetchNotificationsActionSuccess} action
 */
export const fetchNotificationsDashboardSuccess = (response, meta) => ({
  ...fetchNotificationsSuccess(response, meta),
  type: types.FETCH_NOTIFICATIONS_DASHBOARD_SUCCESS,
});

/**
 * @param {string} message The error message
 * @param {Object} [meta]
 * @returns {import('modules/global/generic.types').ErrorAction} action
 */
export const fetchNotificationsDashboardFailure = (message, meta) => ({
  ...fetchNotificationsFailure(message, meta),
  type: types.FETCH_NOTIFICATIONS_DASHBOARD_FAILURE,
});

/**
 * @returns {import('./types').ResetNotificationsDashboardAction}
 */
export const resetNotificationsDashboard = () => ({
  type: types.RESET_NOTIFICATIONS_DASHBOARD,
});

/**
 * @param {string} userId
 * @param {Object} [meta]
 * @returns {import('./types').FetchNotificationsAction} action
 */
export const fetchNotificationsDropdown = (userId, meta) => ({
  ...fetchNotifications(
    {
      userId,
      page: {
        size: 5,
      },
    },
    meta
  ),
  type: types.FETCH_DROPDOWN_NOTIFICATIONS,
});

/**
 * @param {import('./types').FetchNotificationsSuccessParams} response
 * @param {Object} [meta]
 * @returns {import('./types').FetchNotificationsActionSuccess} action
 */
export const fetchNotificationsDropdownSuccess = (response, meta) => ({
  ...fetchNotificationsSuccess(response, meta),
  type: types.FETCH_DROPDOWN_NOTIFICATIONS_SUCCESS,
});

/**
 * @param {string} message The error message
 * @param {Object} [meta]
 * @returns {import('modules/global/generic.types').ErrorAction} action
 */
const fetchNotificationsDropdownFailure = (message, meta) => ({
  ...fetchNotificationsFailure(message, meta),
  type: types.FETCH_DROPDOWN_NOTIFICATIONS_FAILURE,
});

/**
 * @param {string} typeSource
 * @returns {function}
 */
export const genericFetchNotificationsSuccess = typeSource => {
  switch (typeSource) {
    case types.FETCH_NOTIFICATIONS_DASHBOARD:
      return fetchNotificationsDashboardSuccess;
    case types.FETCH_DROPDOWN_NOTIFICATIONS:
      return fetchNotificationsDropdownSuccess;
    default:
      return fetchNotificationsSuccess;
  }
};

/**
 * @param {string} typeSource
 * @returns {function}
 */
export const genericFetchNotificationsFailure = typeSource => {
  switch (typeSource) {
    case types.FETCH_NOTIFICATIONS_DASHBOARD:
      return fetchNotificationsDashboardFailure;
    case types.FETCH_DROPDOWN_NOTIFICATIONS:
      return fetchNotificationsDropdownFailure;
    default:
      return fetchNotificationsFailure;
  }
};

/**
 * @param {string} userId
 * @param {Object} [meta]
 * @returns {import('./types').FetchUnreadNotificationsCountAction} action
 */
export const fetchUnreadNotificationsCount = (userId, meta) => ({
  type: types.FETCH_UNREAD_NOTIFICATIONS_COUNT,
  payload: {
    request: {
      method: 'GET',
      url: `${USERS_API_URL}/${userId}/notifications/unread`,
    },
  },
  meta,
});
/**
 * @param {Array<string>} notificationsIds The ids of unread notifications
 * @param {Object} [meta]
 * @returns {import('./types').FetchUnreadNotificationsCountActionSuccess} action
 */
export const fetchUnreadNotificationsCountSuccess = (notificationsIds, meta) => ({
  type: types.FETCH_UNREAD_NOTIFICATIONS_COUNT_SUCCESS,
  payload: notificationsIds,
  meta,
});
/**
 * @param {string} message The error message
 * @param {Object} [meta]
 * @returns {import('modules/global/generic.types').ErrorAction} action
 */
export const fetchUnreadNotificationsCountFailure = (message, meta) => ({
  type: types.FETCH_UNREAD_NOTIFICATIONS_COUNT_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {import('./types').PatchNotificationPayload} payload
 * @param {Object} [meta]
 * @returns {import('./types').MarkAsReadAction} action
 */
export const markAsRead = ({ userId, notificationId }, meta) => ({
  type: types.MARK_AS_READ,
  payload: {
    request: {
      method: 'PATCH',
      url: `${USERS_API_URL}/${userId}/notifications/${notificationId}/status`,
      data: {
        status: STATUSES.READ,
      },
    },
    notificationId,
  },
  meta,
});
/**
 * @param {string} notificationId The id of the notification successfully marked as read.
 * @param {*} meta
 * @returns {import('./types').MarkAsReadActionSuccess} action
 */
export const markAsReadSuccess = (notificationId, meta) => ({
  type: types.MARK_AS_READ_SUCCESS,
  payload: notificationId,
  meta,
});

/**
 * @param {string} message The error message
 * @param {string} notificationId The notification id
 * @param {Object} [meta]
 * @returns {import('./types').NotificationErrorAction<Object>} action
 */
export const markAsReadFailure = (message, notificationId, meta) => ({
  type: types.MARK_AS_READ_FAILURE,
  payload: { message, notificationId },
  error: true,
  meta,
});

/**
 * @param {import('./types').PatchNotificationPayload} payload
 * @param {Object} [meta]
 * @returns {import('./types').MarkAsUnreadAction} action
 */
export const markAsUnread = ({ userId, notificationId }, meta) => ({
  type: types.MARK_AS_UNREAD,
  payload: {
    request: {
      method: 'PATCH',
      url: `${USERS_API_URL}/${userId}/notifications/${notificationId}/status`,
      data: {
        status: STATUSES.UNREAD,
      },
    },
    notificationId,
  },
  meta,
});
/**
 * @param {string} notificationId The id of the notification successfully marked as read.
 * @param {Object} meta
 * @returns {import('./types').MarkAsUnreadActionSuccess} action
 */
export const markAsUnreadSuccess = (notificationId, meta) => ({
  type: types.MARK_AS_UNREAD_SUCCESS,
  payload: notificationId,
  meta,
});
/**
 * @param {string} message The error message
 * @param {string} notificationId The notification id
 * @param {Object} [meta]
 * @returns {import('./types').NotificationErrorAction<Object>} action
 */
export const markAsUnreadFailure = (message, notificationId, meta) => ({
  type: types.MARK_AS_UNREAD_FAILURE,
  payload: { message, notificationId },
  error: true,
  meta,
});

/**
 * @param {string} userId
 * @param {Object} [meta]
 * @returns {import('./types').MarkAllAsReadAction} action
 */
export const markAllAsRead = (userId, meta) => ({
  type: types.MARK_ALL_AS_READ,
  payload: {
    request: {
      method: 'POST',
      url: `${USERS_API_URL}/${userId}/notifications/read`,
    },
  },
  meta,
});
/**
 * @param {null} _ unused at the moment
 * @param {Object} [meta]
 * @returns {import('./types').MarkAllAsReadActionSuccess} action
 */
export const markAllAsReadSuccess = (_, meta) => ({
  type: types.MARK_ALL_AS_READ_SUCCESS,
  payload: null,
  meta,
});
/**
 * @param {string} message The error message
 * @param {Object} [meta]
 * @returns {import('modules/global/generic.types').ErrorAction} action
 */
export const markAllAsReadFailure = (message, meta) => ({
  type: types.MARK_ALL_AS_READ_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {string} userId The user id of which to fetch configuration.
 * @param {Object} [meta]
 * @returns {import('./types').FetchNotificationsConfigurationAction} action
 */
export const fetchConfiguration = (userId, meta) => ({
  type: types.FETCH_CONFIGURATION,
  payload: {
    request: {
      method: 'GET',
      url: `${USERS_API_URL}/${userId}/notifications/config`,
    },
    userId,
  },
  meta,
});
/**
 * @param {import('./types').NotificationsConfigurationPayload} payload The notifications configuration
 * @param {Object} [meta]
 * @returns {import('./types').FetchNotificationsConfigurationActionSuccess} action
 */
export const fetchConfigurationSuccess = ({ userId, configuration }, meta) => ({
  type: types.FETCH_CONFIGURATION_SUCCESS,
  payload: {
    userId,
    configuration,
  },
  meta,
});
/**
 * @param {string} message The error message
 * @param {Object} [meta]
 * @returns {import('modules/global/generic.types').ErrorAction} action
 */
export const fetchConfigurationFailure = (message, meta) => ({
  type: types.FETCH_CONFIGURATION_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {import('./types').NotificationsConfigurationPayload} payload
 * @param {Object} [meta]
 * @returns {import('./types').PatchNotificationsConfigurationAction}  action
 */
export const patchConfiguration = ({ userId, notificationId, configuration }, meta) => ({
  type: types.PATCH_CONFIGURATION,
  payload: {
    request: {
      method: 'PATCH',
      url: `${USERS_API_URL}/${userId}/notifications/config`,
      data: configuration,
    },
    notificationId,
    userId,
  },
  meta,
});
/**
 * @param {import('./types').NotificationsConfigurationPayload} payload The patched configuration for the given userId
 * @param {Object} [meta]
 * @returns {import('./types').PatchNotificationsConfigurationActionSuccess} action
 */
export const patchConfigurationSuccess = ({ userId, notificationId, configuration }, meta) => ({
  type: types.PATCH_CONFIGURATION_SUCCESS,
  payload: {
    userId,
    configuration,
    notificationId,
  },
  meta,
});
/**
 * @param {string} message The error message
 * @param {string} notificationId The notification id
 * @param {Object} [meta]
 * @returns {import('./types').NotificationErrorAction<Object>} action
 */
export const patchConfigurationFailure = (message, notificationId, meta) => ({
  type: types.PATCH_CONFIGURATION_FAILURE,
  payload: { message, notificationId },
  error: true,
  meta,
});

/**
 * @param {import('modules/global/typing/enums/extensions.enum').ExtensionEnum | import('modules/global/typing/enums/features.enum').FeaturesEnum} extension
 * @param {Object} [meta={}]
 * @returns {import('./types').SetConfigurationExtensionAction}
 */
export const setConfigurationExtension = (extension, meta = {}) => ({
  type: types.SET_SETTINGS_EXTENSION,
  payload: extension,
  meta,
});

/**
 *
 * @param {import('./types').NotificationExtension} extension
 * @param {import('./types').NotificationSetting} setting
 * @param notificationId
 * @param {Object} [meta={}]
 * @returns {import('./types').DisabledNotificationSettingQuicklyAction}
 */
export const disabledNotificationSettingQuickly = (extension, setting, notificationId, meta = {}) => ({
  type: types.DISABLED_NOTIFICATION_SETTING,
  payload: { extension, setting, notificationId },
  meta,
});

/**
 * @param {string} userId
 * @param {Object=} meta
 * @returns {import('./types').OpenPermissionModal}
 * */
export const openPermissionModal = (userId, meta = {}) => ({
  type: types.OPEN_PERMISSION_MODAL,
  payload: { userId },
  meta,
});

/**
 * @param {string} userId
 * @param {Object=} meta
 * @returns {import('./types').RefusePermission}
 * */
export const refusePermission = (userId, meta = {}) => ({
  type: types.REFUSE_PERMISSION,
  payload: { userId },
  meta,
});

/**
 * @param {undefined} payload unused for the moment
 * @param {Object=} meta
 * @returns {import('./types').AcceptPermission}
 * */
export const acceptPermission = (payload, meta = {}) => ({
  type: types.ACCEPT_PERMISSION,
  payload,
  meta,
});

/**
 * @param {string} userId
 * @param {Object} meta Unused at the moment
 * @returns {import('./types').NotificationBlockedAction}
 */
export const notificationsBlocked = (userId, meta = {}) => ({
  type: types.NOTIFICATIONS_BLOCKED,
  payload: { userId },
  meta,
});
